const text = (value) => (value == null ? '' : String(value).trim());
const comparable = (value) => text(value).toLowerCase().replace(/\s+/g, ' ');
export const DELIVERY_DETAIL_FIELDS = [
  'doorcode',
  'suite',
  'unit',
  'apartment',
  'room',
  'building',
  'company',
  'subdivision',
  'floor',
  'notes',
];
export const deliveryDetails = (addition) =>
  Object.fromEntries(
    DELIVERY_DETAIL_FIELDS.map((field) => [field, text(addition?.[field])])
  );
const coordinate = (value) =>
  !['number', 'string'].includes(typeof value) ||
  text(value) === '' ||
  !Number.isFinite(Number(value))
    ? null
    : Number(value);

// Do not parse the display label into structured data: it is not proof of a
// deliverable destination, and can be left over from an earlier selection.
export function normalizeDeliveryAddress(value) {
  if (!value || typeof value !== 'object') return null;
  const source =
    value.address && typeof value.address === 'object'
      ? { ...value, ...value.address }
      : value;
  const location = source.location || source.coordinates || {};
  const lat =
    typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng =
    typeof location.lng === 'function' ? location.lng() : location.lng;
  const coordinates =
    location.coordinates || (Array.isArray(location) ? location : []);
  const address = text(source.address);
  const city = text(source.city);
  const state = text(source.state);
  const zipcode = text(source.zipcode || source.zip);
  return {
    ...source,
    address,
    city,
    state,
    zipcode,
    entered_address:
      text(source.entered_address) ||
      [address, city, [state, zipcode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', '),
    latitude: coordinate(
      source.latitude ??
        source.lat ??
        location.latitude ??
        lat ??
        coordinates[1]
    ),
    longitude: coordinate(
      source.longitude ??
        source.lng ??
        location.longitude ??
        lng ??
        coordinates[0]
    ),
    id: source.address_id ?? source.id ?? null,
    address_id: source.address_id ?? source.id ?? null,
    user_id: source.user_id ?? source.userId ?? null,
  };
}

export function validateDeliveryAddress(value) {
  const address = normalizeDeliveryAddress(value);
  const missing = [
    !address?.address && 'street address',
    !address?.city && 'city',
    !address?.state && 'state',
    !address?.zipcode && 'ZIP code',
    (address?.latitude == null ||
      address?.longitude == null ||
      Math.abs(address.latitude) > 90 ||
      Math.abs(address.longitude) > 180) &&
      'map location',
  ].filter(Boolean);
  if (missing.length) {
    throw new Error(
      `Please confirm the ${missing.join(
        ', '
      )}. Choose an address result or a saved address to supply the missing details.`
    );
  }
  return address;
}

export function sameDeliveryDestination(left, right) {
  return (
    ['address', 'city', 'state', 'zipcode'].every(
      (field) =>
        comparable(left[field]) &&
        comparable(left[field]) === comparable(right[field])
    ) &&
    DELIVERY_DETAIL_FIELDS.every(
      (field) => text(left.addition?.[field]) === text(right.addition?.[field])
    ) &&
    (left.latitude == null ||
      left.longitude == null ||
      (right.latitude != null &&
        right.longitude != null &&
        Math.abs(left.latitude - right.latitude) < 0.0005 &&
        Math.abs(left.longitude - right.longitude) < 0.0005))
  );
}
export function deliveryAddressError(error) {
  // Never echo a backend payload (which can include address/account data).
  if (error?.status === 401 || error?.response?.status === 401)
    return 'Your sign-in session has expired. Please sign in again before confirming your delivery address. Your cart has not changed.';
  if (error?.deliveryValidation) return error.message;
  return 'We could not save your delivery address. Please try Update again, or choose a saved address. Your cart has not changed.';
}
